import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { Input } from '@/components/ui-kit/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import {
  useCreateMbmProject,
  useGetMbmCustomers,
  useUpdateMbmProject,
} from '../../hooks/use-mbm-projects';
import { mbmProjectFormSchema, MbmProjectFormValues } from '../../schemas/mbm-project-form-schema';
import { mbmProjectExists } from '../../services/mbm-project.service';
import {
  MBM_ACTIVE_STATUS,
  MBM_INACTIVE_STATUS,
  MbmCustomer,
  MbmProject,
} from '../../types/mbm-project.types';

export interface MbmProjectFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project?: MbmProject | null;
}

const emptyValues: MbmProjectFormValues = {
  ProjectId: '',
  ProjectName: '',
  CustomerId: '',
  CustomerName: '',
  Status: MBM_ACTIVE_STATUS,
};

/**
 * One dialog for create and edit. A project is identified by its customer id and project id
 * together (the legacy upsert key) and timesheet entries store those ids, so both are only
 * settable at creation and locked afterwards.
 */
export const MbmProjectFormDialog = ({
  open,
  onOpenChange,
  project,
}: MbmProjectFormDialogProps) => {
  const { t } = useTranslation();
  const isEditMode = Boolean(project);

  const { data: customersData } = useGetMbmCustomers();
  const customers = (customersData as MbmCustomer[] | undefined) ?? [];
  const { mutateAsync: createProject, isPending: isCreating } = useCreateMbmProject();
  const { mutateAsync: updateProject, isPending: isUpdating } = useUpdateMbmProject();
  const isSubmitting = isCreating || isUpdating;

  const form = useForm<MbmProjectFormValues>({
    resolver: async (data, context, options) => {
      const result = await zodResolver(mbmProjectFormSchema)(data, context, options);
      if (result.errors) {
        result.errors = Object.fromEntries(
          Object.entries(result.errors).map(([key, error]) => [
            key,
            { ...error, message: t((error as { message?: string }).message ?? '') },
          ])
        );
      }
      return result;
    },
    defaultValues: emptyValues,
  });

  useEffect(() => {
    if (!open) return;
    form.reset(
      project
        ? {
            ProjectId: project.ProjectId,
            ProjectName: project.ProjectName,
            CustomerId: project.CustomerId,
            CustomerName: project.CustomerName,
            Status: project.Status || MBM_INACTIVE_STATUS,
          }
        : emptyValues
    );
  }, [open, project, form]);

  // A status MBM sent that is not Active/Inactive must stay selectable, or editing the name of
  // such a project would silently rewrite its status.
  const statusOptions = useMemo(() => {
    const options = [MBM_ACTIVE_STATUS, MBM_INACTIVE_STATUS];
    if (project?.Status && !options.includes(project.Status)) options.push(project.Status);
    return options;
  }, [project]);

  const statusLabel = (status: string) => {
    if (status === MBM_ACTIVE_STATUS) return t('ACTIVE');
    if (status === MBM_INACTIVE_STATUS) return t('INACTIVE');
    return status;
  };

  // Typing the id of a customer that already has projects fills in its name, so the same customer
  // is not entered under two spellings.
  const fillKnownCustomerName = (customerId: string) => {
    if (isEditMode || form.getValues('CustomerName')) return;
    const known = customers.find((customer) => customer.CustomerId === customerId.trim());
    if (known) form.setValue('CustomerName', known.CustomerName, { shouldValidate: true });
  };

  const handleSubmit = async (values: MbmProjectFormValues) => {
    if (project) {
      try {
        await updateProject({ project, values });
        onOpenChange(false);
      } catch {
        // Error toast is already shown by the mutation's global error handler.
      }
      return;
    }
    if (await mbmProjectExists(values.CustomerId, values.ProjectId)) {
      form.setError('ProjectId', { message: t('MBM_PROJECT_ALREADY_EXISTS') });
      return;
    }
    try {
      await createProject(values);
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditMode ? t('MBM_PROJECT_EDIT') : t('MBM_PROJECT_CREATE')}</DialogTitle>
          {isEditMode && <DialogDescription>{t('MBM_PROJECT_KEYS_LOCKED')}</DialogDescription>}
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="CustomerId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('MBM_CUSTOMER_ID')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('MBM_CUSTOMER_ID')}
                        disabled={isEditMode}
                        {...field}
                        onBlur={(event) => {
                          field.onBlur();
                          fillKnownCustomerName(event.target.value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="ProjectId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('MBM_PROJECT_ID')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('MBM_PROJECT_ID')} disabled={isEditMode} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="CustomerName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('CUSTOMER_NAME')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('CUSTOMER_NAME')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="ProjectName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('PROJECT_NAME')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('PROJECT_NAME')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="Status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('STATUS')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('STATUS')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {statusOptions.map((status) => (
                        <SelectItem key={status} value={status}>
                          {statusLabel(status)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>{t('MBM_PROJECT_STATUS_HINT')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                {isEditMode ? t('SAVE_CHANGES') : t('CREATE')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
